import { useState, useEffect, useRef, forwardRef } from 'react'
import { XMarkIcon } from '@heroicons/react/16/solid'
import { useDebounce } from '@/utils/fetch'
import apiFetch from '@/services/apiService'
import SuggestionsPortal from '@/components/SuggestionsPortal'

const SuggestionField = ({type = "", nature = "", contactField, originalData = null,
    label = "Contact",
    placeholder = "Rechercher un contact",
  }, forwardedRef) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [loading, setLoading] = useState(false)
  const [selectedContact, setSelectedContact] = useState(null)
  const inputRef = useRef(null)

  // Récupérer les suggestions de contacts
  const fetchSuggestions = useDebounce(async (term) => {
    if (!term || term.length < 2) {
      setSuggestions([])
      return
    }
    
    setLoading(true)
    try {
      let query = { term }
      if(type) { 
        query = {...query, type }
      }
      if(nature) {
        query = {...query, nature }
      }
      
      const response = await apiFetch()
        .url(`contacts/search`)
        .query(query)
        .get()
      
      setSuggestions(response.data || [])
    } catch (error) {
      console.error('Erreur lors de la recherche de contacts:', error)
      setSuggestions([])
    } finally {
      setLoading(false)
    }
  }, 300)

  // Charger les détails du contact sélectionné
  useEffect(() => {
    if(originalData && originalData.nom_complet) {
      setSelectedContact(originalData)
      setSearchTerm(originalData?.nom_complet)
    }
  }, [originalData])

  // Gérer le changement dans le champ de recherche
  const handleInputChange = (e) => {
    const value = e.target.value
    setSearchTerm(value)
    fetchSuggestions(value)
    setShowSuggestions(true)
    
    // Si le champ est vidé, réinitialiser le contact sélectionné
    if (!value) {
      contactField.onChange('')
      setSelectedContact(null)
    }
  }

  // Sélectionner un contact dans les suggestions
  const handleSelectContact = (contact) => {
    contactField.onChange(contact)
    setSearchTerm(contact.nom_complet || '')
    setSelectedContact(contact)
    setShowSuggestions(false)
  } 

  // Réinitialiser le contact sélectionné
  const resetSelectedContact = () => {
    contactField.onChange('')
    setSelectedContact(null)
    setSearchTerm('')
  }

  return (
    <div className="relative suggestions-container">
      <label className="input w-full">
        <span className="label">{label}</span>
        <input
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          placeholder={placeholder}
          className="w-full"
          ref={(el) => {
            inputRef.current = el
            if (forwardedRef) {
              if (typeof forwardedRef === 'function') {
                forwardedRef(el)
              } else if (forwardedRef.current !== undefined) {
                forwardedRef.current = el
              }
            }
          }}
        />
        {loading && <span className="loading loading-spinner loading-sm text-base-content/70 absolute right-2 top-1/2 transform -translate-y-1/2"></span>}
        {!loading && (selectedContact || searchTerm.length > 0) && (
          <button
          type="button"
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-base-content/70 rounded-full w-4 h-4 transition-colors duration-200 ease-in hover:bg-gray-200 focus:outline-none focus:bg-gray-300 dark:text-white dark:hover:bg-dark-500 dark:focus:bg-dark-450 hover:bg-gray-200"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              resetSelectedContact()
            }}
          >
            <XMarkIcon/>
        </button>
        )}
      </label>
      

      <SuggestionsPortal
        show={showSuggestions && (suggestions.length > 0 || (searchTerm.length >= 2 && !loading))}
        anchorRef={inputRef}
      >
      {suggestions.length > 0 ? (
        <div className="absolute z-10 w-full bg-base-100 shadow-lg rounded-lg mt-1 border border-base-300 max-h-60 overflow-y-auto">
          {suggestions.map(contact => (
            <div 
              key={contact.id}
              className="p-2 hover:bg-base-200 cursor-pointer"
              onClick={() => handleSelectContact(contact)}
            >
              <div className="font-medium">{contact.nom_complet}</div>
              <div className="text-sm text-base-content/70">
                {contact.email || contact.telephone ? `${contact.email} / ${contact.telephone}` : 'Aucune information de contact'}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="absolute z-10 w-full bg-base-100 shadow-lg rounded-lg mt-1 border border-base-300 p-2 space-y-2">
          <p>Aucun contact trouvé</p>
          <p className="text-sm">
            Veuillez d'abord <a href="/contacts" className="text-primary hover:underline">créer le contact</a> dans la liste des contacts.
          </p>
        </div>
      )}
      </SuggestionsPortal> 
    </div>
  )
}
export default forwardRef((props, ref) => SuggestionField({...props}, ref))